"use client";

import * as React from "react";
import { Flex } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { AlertDialog, Button } from "./elements.js";
import { getSessionsQueryKey, useRevokeAllSessions } from "../api/endpoint.js";
import { useQueryClient } from "@tanstack/react-query";
import { SaveButton } from "./save-button.js";
import { Translation } from "./i18n/translation.js";

interface LogoutAllSessionsDialogProps extends AlertDialog.RootProps {
  children?: ReactNode;
  currentSessionId: string;
}

export function LogoutAllSessionsDialog({
  open,
  onOpenChange,
  children,
  currentSessionId,
  ...props
}: LogoutAllSessionsDialogProps) {
  const client = useQueryClient();

  const revokeAllSessions = useRevokeAllSessions();

  const onSubmitForm = () => {
    revokeAllSessions.mutate({ data: { currentSessionId } });
  };

  const handleDone = React.useCallback(() => {
    onOpenChange?.(false);

    client.invalidateQueries({
      queryKey: getSessionsQueryKey(),
    });
  }, [client, onOpenChange]);

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange} {...props}>
      <AlertDialog.Content maxWidth="480px">
        <AlertDialog.Title>
          <Translation
            defaultMessage="Sign out of all other devices?"
            id="x//BWg"
            description="Dialog title asking to confirm signing out of all other devices"
          />
        </AlertDialog.Title>
        <AlertDialog.Description>
          <Translation
            defaultMessage="You will be logged out of all other active sessions on other devices, except this one."
            id="GftsX9"
            description="Description explaining all other sessions will be logged out"
          />
        </AlertDialog.Description>

        <Flex gap="3" justify="end" mt="5" asChild>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmitForm();
            }}
          >
            <AlertDialog.Cancel>
              <Button
                variant="secondary"
                disabled={
                  revokeAllSessions.isPending || revokeAllSessions.isSuccess
                }
              >
                <Translation
                  defaultMessage="Cancel"
                  id="Pnmy4H"
                  description="Button to cancel signing out of all devices"
                />
              </Button>
            </AlertDialog.Cancel>

            <SaveButton
              asChild
              loading={revokeAllSessions.isPending}
              done={revokeAllSessions.isSuccess}
              onDone={handleDone}
            >
              <Button variant="destructive" type="submit">
                <Translation
                  defaultMessage="Sign out"
                  id="1Fb48o"
                  description="Button to confirm signing out of all other devices"
                />
              </Button>
            </SaveButton>
          </form>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
}
